import { type ReactNode } from 'react'
import { FormRow } from '@/components/Form'
import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import styles from './WorkspacePostingDialog.module.css'
import cx from 'classnames'

export interface WorkspacePostingDialogProps {
  showing: boolean
  onClose: () => unknown
  title: string
  buttonText: string
  isSubmitDisabled: boolean
  isSubmitPending: boolean
  /**
   * Id of the posting form the footer's submit button submits.
   */
  formId: string
  /**
   * The posting form.
   */
  children?: ReactNode
}

export function WorkspacePostingDialog({
  showing,
  onClose,
  title,
  buttonText,
  isSubmitDisabled,
  isSubmitPending,
  formId,
  children,
}: WorkspacePostingDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>{title}</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>{children}</ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />
          <Button
            label={buttonText}
            type="submit"
            form={formId}
            variant="primary"
            isPending={isSubmitPending}
            isDisabled={isSubmitDisabled}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface WorkspacePostingOutsourcingRowProps {
  isOutsourcing: boolean
  children?: ReactNode
}

/**
 * A form row that is only visible for outsourcing postings.
 */
export function WorkspacePostingOutsourcingRow({
  isOutsourcing,
  children,
}: WorkspacePostingOutsourcingRowProps) {
  return (
    <FormRow className={cx(!isOutsourcing && styles.hideOutsourcing)}>
      {children}
    </FormRow>
  )
}

export interface WorkspacePostingNumericInputsProps {
  children?: ReactNode
}

export function WorkspacePostingNumericInputs({
  children,
}: WorkspacePostingNumericInputsProps) {
  return (
    <FormRow>
      <div className={styles.numericInputContainerGroup}>{children}</div>
    </FormRow>
  )
}

export interface WorkspacePostingTypeOfWorkItem {
  type: string
  label: string
  active: boolean
  onToggle: () => void
}

export interface WorkspacePostingTypeOfWorkFieldProps {
  items: WorkspacePostingTypeOfWorkItem[]
}

export function WorkspacePostingTypeOfWorkField({
  items,
}: WorkspacePostingTypeOfWorkFieldProps) {
  return (
    <FormRow>
      <RadioButtonGroup label="Type of work">
        {items.map((item) => (
          <RadioButtonItem
            active={item.active}
            onPress={item.onToggle}
            key={item.type}
          >
            {item.label}
          </RadioButtonItem>
        ))}
      </RadioButtonGroup>
    </FormRow>
  )
}
